import type { AgentChange, FileSnapshot, ToolExecutionResult, WorkspaceFile, WorkspaceScope } from "../types";
import { db, putTextFile } from "./db";
import { normalizeProjectPath } from "./path";

const MAX_READ_CHARS = 180_000;
const MAX_WRITE_CHARS = 1_000_000;

export const READ_TOOLS = [
  {
    type: "function", name: "list_files",
    description: "List all files in the current task workspace, optionally under one relative directory. Returns paths, sizes, kinds and revisions.",
    strict: true,
    parameters: { type: "object", properties: { path: { type: ["string", "null"], description: "Relative directory prefix, or null for the whole project." } }, required: ["path"], additionalProperties: false },
  },
  {
    type: "function", name: "read_file",
    description: "Read a UTF-8 text file. Always note its revision before writing it.",
    strict: true,
    parameters: { type: "object", properties: { path: { type: "string" } }, required: ["path"], additionalProperties: false },
  },
  {
    type: "function", name: "read_files",
    description: "Read up to 12 UTF-8 text files at once.",
    strict: true,
    parameters: { type: "object", properties: { paths: { type: "array", items: { type: "string" }, maxItems: 12 } }, required: ["paths"], additionalProperties: false },
  },
  {
    type: "function", name: "search_files",
    description: "Search a literal text across the current task's text files. Returns matching lines.",
    strict: true,
    parameters: { type: "object", properties: {
      query: { type: "string" }, path: { type: ["string", "null"], description: "Optional directory prefix." },
      max_results: { type: "integer", minimum: 1, maximum: 100 },
    }, required: ["query", "path", "max_results"], additionalProperties: false },
  },
] as const;

export const WRITE_TOOLS = [
  {
    type: "function", name: "write_file",
    description: "Create or fully replace a UTF-8 text file. Use expected_revision from read_file; use null only for a new file.",
    strict: true,
    parameters: { type: "object", properties: {
      path: { type: "string" }, content: { type: "string" }, expected_revision: { type: ["integer", "null"] },
    }, required: ["path", "content", "expected_revision"], additionalProperties: false },
  },
  {
    type: "function", name: "replace_in_file",
    description: "Replace one exact, unique text fragment in a file. Fails if the fragment is absent or appears more than once.",
    strict: true,
    parameters: { type: "object", properties: {
      path: { type: "string" }, old_text: { type: "string" }, new_text: { type: "string" }, expected_revision: { type: "integer" },
    }, required: ["path", "old_text", "new_text", "expected_revision"], additionalProperties: false },
  },
  {
    type: "function", name: "move_file",
    description: "Move or rename one file. The destination must not exist.",
    strict: true,
    parameters: { type: "object", properties: {
      from: { type: "string" }, to: { type: "string" }, expected_revision: { type: "integer" },
    }, required: ["from", "to", "expected_revision"], additionalProperties: false },
  },
  {
    type: "function", name: "delete_file",
    description: "Delete one file after reading it. This operation can be undone from the UI.",
    strict: true,
    parameters: { type: "object", properties: {
      path: { type: "string" }, expected_revision: { type: "integer" },
    }, required: ["path", "expected_revision"], additionalProperties: false },
  },
] as const;

export const WEB_SEARCH_TOOL = {
  type: "function",
  name: "search_web",
  description: "Search the live public web for current or time-sensitive information. Use a short, standalone query containing public information only. The result includes clickable source citations.",
  strict: true,
  parameters: {
    type: "object",
    properties: {
      query: { type: "string", description: "A standalone public web search query, without project files, source code, secrets, or API keys." },
    },
    required: ["query"],
    additionalProperties: false,
  },
} as const;

export const ALL_TOOLS = [...READ_TOOLS, ...WRITE_TOOLS];

function snapshot(file: WorkspaceFile | undefined): FileSnapshot | null {
  if (!file) return null;
  return { kind: file.kind, content: file.content, mimeType: file.mimeType, size: file.size, revision: file.revision };
}

function json(data: unknown): string {
  return JSON.stringify(data);
}

async function assertScope(scope: WorkspaceScope): Promise<void> {
  const chat = await db.chats.get(scope.chatId);
  if (!chat || chat.projectId !== scope.projectId) throw new Error("Tâche introuvable.");
}

async function readOne(scope: WorkspaceScope, rawPath: string) {
  const path = normalizeProjectPath(rawPath);
  const file = await db.taskFiles.get([scope.chatId, path]);
  if (!file) throw new Error(`Fichier introuvable : ${path}`);
  if (file.kind !== "text") return { path, kind: file.kind, mime_type: file.mimeType, size: file.size, revision: file.revision, error: "binary_file" };
  const content = file.content as string;
  return { path, kind: file.kind, size: file.size, revision: file.revision,
    content: content.slice(0, MAX_READ_CHARS), truncated: content.length > MAX_READ_CHARS };
}

export async function executeTool(scope: WorkspaceScope, name: string, args: Record<string, unknown>): Promise<ToolExecutionResult> {
  await assertScope(scope);
  if (name === "list_files") {
    const prefix = args.path === null ? "" : normalizeProjectPath(String(args.path));
    const files = await db.taskFiles.where("chatId").equals(scope.chatId).sortBy("path");
    const filtered = prefix ? files.filter((file) => file.path === prefix || file.path.startsWith(`${prefix}/`)) : files;
    return { label: `Liste des fichiers${prefix ? ` · ${prefix}` : ""}`, changes: [],
      output: json({ files: filtered.map(({ path, kind, size, revision }) => ({ path, kind, size, revision })) }) };
  }

  if (name === "read_file") {
    const result = await readOne(scope, String(args.path));
    return { label: `Lecture · ${result.path}`, changes: [], output: json(result) };
  }

  if (name === "read_files") {
    const paths = Array.isArray(args.paths) ? args.paths.slice(0, 12).map(String) : [];
    const results = [];
    for (const path of paths) results.push(await readOne(scope, path));
    return { label: `Lecture de ${results.length} fichiers`, changes: [], output: json({ files: results }) };
  }

  if (name === "search_files") {
    const query = String(args.query ?? "");
    if (!query || query.length > 500) throw new Error("La recherche doit contenir entre 1 et 500 caractères.");
    const prefix = args.path === null ? "" : normalizeProjectPath(String(args.path));
    const limit = Math.min(100, Math.max(1, Number(args.max_results) || 30));
    const needle = query.toLocaleLowerCase();
    const files = await db.taskFiles.where("chatId").equals(scope.chatId).toArray();
    const matches: Array<{ path: string; line: number; text: string }> = [];
    for (const file of files) {
      if (file.kind !== "text" || (prefix && !file.path.startsWith(prefix))) continue;
      const lines = (file.content as string).split("\n");
      for (let index = 0; index < lines.length && matches.length < limit; index += 1) {
        if (lines[index].toLocaleLowerCase().includes(needle)) matches.push({ path: file.path, line: index + 1, text: lines[index].slice(0, 500) });
      }
      if (matches.length >= limit) break;
    }
    return { label: `Recherche · ${query}`, changes: [], output: json({ matches, truncated: matches.length >= limit }) };
  }

  if (name === "write_file") {
    const path = normalizeProjectPath(String(args.path));
    const content = String(args.content ?? "");
    if (content.length > MAX_WRITE_CHARS) throw new Error("Le contenu dépasse la limite de 1 000 000 caractères.");
    const before = await db.taskFiles.get([scope.chatId, path]);
    const expected = args.expected_revision === null ? null : Number(args.expected_revision);
    const after = await putTextFile(scope, path, content, expected);
    return { label: `${before ? "Modification" : "Création"} · ${path}`, changes: [{ path, before: snapshot(before), after: snapshot(after) }],
      output: json({ ok: true, path, revision: after.revision, size: after.size }) };
  }

  if (name === "replace_in_file") {
    const path = normalizeProjectPath(String(args.path));
    const file = await db.taskFiles.get([scope.chatId, path]);
    if (!file || file.kind !== "text") throw new Error(`Fichier texte introuvable : ${path}`);
    const oldText = String(args.old_text ?? "");
    const newText = String(args.new_text ?? "");
    if (!oldText) throw new Error("old_text ne peut pas être vide.");
    const content = file.content as string;
    const first = content.indexOf(oldText);
    if (first < 0) throw new Error("Le fragment à remplacer est introuvable.");
    if (content.indexOf(oldText, first + oldText.length) >= 0) throw new Error("Le fragment apparaît plusieurs fois ; fournis davantage de contexte.");
    const nextContent = `${content.slice(0, first)}${newText}${content.slice(first + oldText.length)}`;
    if (nextContent.length > MAX_WRITE_CHARS) throw new Error("Le fichier résultant est trop volumineux.");
    const after = await putTextFile(scope, path, nextContent, Number(args.expected_revision));
    return { label: `Modification ciblée · ${path}`, changes: [{ path, before: snapshot(file), after: snapshot(after) }],
      output: json({ ok: true, path, revision: after.revision, size: after.size }) };
  }

  if (name === "delete_file") {
    const path = normalizeProjectPath(String(args.path));
    const before = await db.taskFiles.get([scope.chatId, path]);
    if (!before) throw new Error(`Fichier introuvable : ${path}`);
    if (before.revision !== Number(args.expected_revision)) throw new Error(`Conflit de version pour ${path}.`);
    await db.transaction("rw", db.taskFiles, db.projects, db.chats, async () => {
      const now = Date.now();
      await db.taskFiles.delete([scope.chatId, path]);
      await db.projects.update(scope.projectId, { updatedAt: now });
      await db.chats.update(scope.chatId, { updatedAt: now });
    });
    return { label: `Suppression · ${path}`, changes: [{ path, before: snapshot(before), after: null }], output: json({ ok: true, path }) };
  }

  if (name === "move_file") {
    const from = normalizeProjectPath(String(args.from));
    const to = normalizeProjectPath(String(args.to));
    const result = await db.transaction("rw", db.taskFiles, db.projects, db.chats, async () => {
      const source = await db.taskFiles.get([scope.chatId, from]);
      if (!source) throw new Error(`Fichier introuvable : ${from}`);
      if (source.revision !== Number(args.expected_revision)) throw new Error(`Conflit de version pour ${from}.`);
      if (await db.taskFiles.get([scope.chatId, to])) throw new Error(`La destination existe déjà : ${to}`);
      const moved: WorkspaceFile = { ...source, path: to, revision: source.revision + 1, updatedAt: Date.now() };
      await db.taskFiles.delete([scope.chatId, from]);
      await db.taskFiles.add(moved);
      await db.projects.update(scope.projectId, { updatedAt: moved.updatedAt });
      await db.chats.update(scope.chatId, { updatedAt: moved.updatedAt });
      return { source, moved };
    });
    return { label: `Déplacement · ${from} → ${to}`, changes: [
      { path: from, before: snapshot(result.source), after: null },
      { path: to, before: null, after: snapshot(result.moved) },
    ], output: json({ ok: true, from, to, revision: result.moved.revision }) };
  }

  throw new Error(`Outil inconnu : ${name}`);
}

export function collapseChanges(changes: AgentChange[]): AgentChange[] {
  const byPath = new Map<string, AgentChange>();
  for (const change of changes) {
    const current = byPath.get(change.path);
    byPath.set(change.path, current ? { path: change.path, before: current.before, after: change.after } : change);
  }
  return [...byPath.values()].filter((change) => JSON.stringify(change.before) !== JSON.stringify(change.after));
}

export async function undoChanges(scope: WorkspaceScope, changes: AgentChange[]): Promise<void> {
  await assertScope(scope);
  await db.transaction("rw", db.taskFiles, db.projects, db.chats, async () => {
    for (const change of [...changes].reverse()) {
      const current = await db.taskFiles.get([scope.chatId, change.path]);
      if (change.after) {
        if (!current || current.revision !== change.after.revision) {
          throw new Error(`Impossible d’annuler ${change.path} : le fichier a changé depuis l’action de l’agent.`);
        }
      } else if (current) {
        throw new Error(`Impossible d’annuler ${change.path} : ce chemin a été recréé depuis l’action de l’agent.`);
      }
      if (!change.before) {
        await db.taskFiles.delete([scope.chatId, change.path]);
      } else {
        const now = Date.now();
        await db.taskFiles.put({ ...scope, path: change.path, ...change.before, revision: (change.after?.revision ?? change.before.revision) + 1,
          createdAt: now, updatedAt: now });
      }
    }
    const now = Date.now();
    await db.projects.update(scope.projectId, { updatedAt: now });
    await db.chats.update(scope.chatId, { updatedAt: now });
  });
}
